import {
  encodeDeviceHeaderValue,
  normalizeDeviceId,
  normalizeMacAddress,
  normalizeMacConsent,
  SIGN_IN_DEVICE_HEADERS,
} from '@featit/shared/credentialClient';
import { isDesktopRuntime } from '@/desktop/desktopDetection';

/**
 * Which install a sign-in comes from (I-1034, `concept-machine-client-credentials.md` §7.2.3).
 *
 * A browser keeps a random id of its own, beside — never inside — the stored session, so it outlives
 * signing out: the next sign-in from this browser then replaces the session the last one left
 * behind instead of adding a row beside it. It identifies nothing but this browser profile to this
 * deployment. The Mac app asks its shell instead, which keeps one install id in its own app data and
 * knows the computer's name.
 */
export const INSTALL_ID_STORAGE_KEY = 'featit.installId';

/** What the desktop shell answers `desktop_device_identity` with. */
interface IDesktopDeviceIdentity {
  id: string;
  name: string | null;
  /** I-1034 AC-6: the primary interface's hardware address, and what macOS calls it. */
  mac?: string | null;
  macInterface?: string | null;
  /** F-0555: the person's answer about reading it; absent from a shell older than the consent. */
  macConsent?: string | null;
}

function browserInstallId(): string | null {
  try {
    const stored = normalizeDeviceId(window.localStorage.getItem(INSTALL_ID_STORAGE_KEY));
    if (stored) return stored;
    const minted = crypto.randomUUID();
    window.localStorage.setItem(INSTALL_ID_STORAGE_KEY, minted);
    return minted;
  } catch {
    // Storage refused (a locked-down profile): the sign-in still works, it just replaces nothing.
    return null;
  }
}

async function desktopIdentity(): Promise<IDesktopDeviceIdentity | null> {
  try {
    const { getDesktopDeviceIdentity } = await import('@/desktop/desktopRuntime');
    return await getDesktopDeviceIdentity();
  } catch {
    // An older shell without the command: fall back to the webview's own install id.
    return null;
  }
}

/**
 * The address, only as the shell gave it — which it does only with consent (F-0555) — and the
 * person's answer, so the server can see it and forget an address it was told to.
 */
function interfaceHeaders(desktop: IDesktopDeviceIdentity | null): Record<string, string> {
  const consent = normalizeMacConsent(desktop?.macConsent);
  const said: Record<string, string> = consent
    ? { [SIGN_IN_DEVICE_HEADERS.macConsent]: consent }
    : {};
  const mac = consent === 'withheld' ? null : normalizeMacAddress(desktop?.mac);
  if (!mac) return said;
  return {
    ...said,
    [SIGN_IN_DEVICE_HEADERS.mac]: mac,
    ...(desktop?.macInterface
      ? { [SIGN_IN_DEVICE_HEADERS.macInterface]: encodeDeviceHeaderValue(desktop.macInterface) }
      : {}),
  };
}

/** The headers every sign-in request carries. Never throws: naming the device is not a gate. */
export async function signInDeviceHeaders(): Promise<Record<string, string>> {
  const desktop = isDesktopRuntime() ? await desktopIdentity() : null;
  const id = normalizeDeviceId(desktop?.id) ?? browserInstallId();
  const headers: Record<string, string> = {};
  if (id) headers[SIGN_IN_DEVICE_HEADERS.id] = id;
  if (desktop?.name) headers[SIGN_IN_DEVICE_HEADERS.name] = encodeDeviceHeaderValue(desktop.name);
  return { ...headers, ...interfaceHeaders(desktop) };
}

/**
 * The headers a refresh carries (I-1034 AC-6): in the Mac app, the interface address it is on now,
 * so its entry stays current when the Mac changes networks. Nothing in a browser.
 */
export async function refreshDeviceHeaders(): Promise<Record<string, string>> {
  return isDesktopRuntime() ? interfaceHeaders(await desktopIdentity()) : {};
}
